import { useEffect } from 'react';
import { useAudioStore } from '@/hooks/useAudioStore';
import { isMobileApp } from '@/utils/mobileBridge';

/**
 * Native → Web sync hook.
 *
 * Sluša `suton-native-event` događaje koje SUTON Radio native WebView wrapper
 * (Expo + react-native-track-player) emituje preko `injectJavaScript`. Tipovi:
 *
 *   - `native-play`   → korisnik je tap-nuo Play sa lock screen-a / Bluetooth-a
 *                       → sync isPlaying = true
 *   - `native-pause`  → korisnik je tap-nuo Pause sa lock screen-a / Bluetooth-a
 *                       → sync isPlaying = false
 *   - `native-stop`   → korisnik je tap-nuo Stop sa lock screen-a / notifikacije
 *                       → sync isPlaying = false (currentStation se ne dira da
 *                          UI ostane u "stand-by" stanju — može odmah resume-ati)
 *   - `native-error`  → samo logiraj, ne diraj store
 *
 * U PWA / browser mod-u hook je NoOp (early return jer `isMobileApp()` = false).
 *
 * Mora biti pozvan jednom globalno (najbolje u App.tsx pored `useZenoMetadata`,
 * `useAzuraCastMetadata`).
 */

type NativeEventType =
  | 'native-play'
  | 'native-pause'
  | 'native-stop'
  | 'native-error'
  | 'native-eq-result'; // R6.2: native EQ apply result + diagnostics

interface NativeEventDetail {
  type: NativeEventType;
  data?: Record<string, unknown> | null;
}

function isNativeEventDetail(v: unknown): v is NativeEventDetail {
  if (typeof v !== 'object' || v === null) return false;
  const t = (v as { type?: unknown }).type;
  return (
    t === 'native-play' ||
    t === 'native-pause' ||
    t === 'native-stop' ||
    t === 'native-error' ||
    t === 'native-eq-result'
  );
}

/**
 * R6.2: globalni store za posljednji native EQ rezultat (debug area u
 * EqualizerModal sluša ovo). Ne ide u Zustand jer se ne persist-uje i ne
 * potrebuje svuda.
 */
type EqResultListener = (data: Record<string, unknown> | null) => void;
const eqResultListeners = new Set<EqResultListener>();

export function subscribeNativeEqResult(listener: EqResultListener): () => void {
  eqResultListeners.add(listener);
  return () => { eqResultListeners.delete(listener); };
}

export function useNativeBridge(): void {
  useEffect(() => {
    if (!isMobileApp()) return;

    const handler = (event: Event): void => {
      const ce = event as CustomEvent<unknown>;
      const detail = ce.detail;
      if (!isNativeEventDetail(detail)) return;

      const setPlaying = useAudioStore.getState().setPlaying;

      switch (detail.type) {
        case 'native-play':
          setPlaying(true);
          break;
        case 'native-pause':
          setPlaying(false);
          break;
        case 'native-stop':
          // Ne brišemo currentStation — korisnik može odmah resume-ati istu stanicu
          // sa Play dugmeta u UI-u ili sa lock screen-a.
          setPlaying(false);
          break;
        case 'native-error':
          console.warn('[useNativeBridge] native-error:', detail.data);
          break;
        case 'native-eq-result':
          // R6.2: forward na EqualizerModal debug area
          eqResultListeners.forEach((fn) => {
            try { fn(detail.data ?? null); } catch { /* noop */ }
          });
          break;
      }
    };

    window.addEventListener('suton-native-event', handler);
    return () => {
      window.removeEventListener('suton-native-event', handler);
    };
  }, []);
}
